import { create } from 'zustand';
import { addChatMessage, getChatMessages, getChatbotWelcome, navigateChatbot, updateLastRead, getReadDetails, getThreadReplies, editChatMessage, getOrgMembers, searchChatMessages, getMessageContext, pinChatMessage, getPinnedChatMessages } from '../services/chat.service';
import { ChatMessage, AddChatMessageRequest, ChatbotOption, ChatbotNavigateRequest, ReadDetailsResponse, MonthOption, OrgMember, ChatSearchResult, MessageDateFilter } from '../types/chat.type';
import { chatWebSocketService } from '../services/websocket.service';
import { trackChatMessageSent, ChatMessageType } from '../analytics/events';

export const CHAT_PAGE_SIZE = 50;

interface ChatState {
  messages: ChatMessage[];
  currentOrgId: string | null;
  isLoading: boolean;
  error: string | null;
  isWebSocketConnected: boolean;
  _wsUnsubscribers: (() => void)[];
  // Chatbot state
  isTreeMode: boolean;
  currentNodeId: string | null;
  chatbotTitle: string;
  chatbotMessage: string;
  chatbotOptions: ChatbotOption[];
  showBackToMain: boolean;
  // Read receipts state
  organizationMembersCount: number;
  // Thread state
  isThreadOpen: boolean;
  activeThreadParentId: string | null;
  threadParentMessage: ChatMessage | null;
  threadReplies: ChatMessage[];
  isLoadingThread: boolean;
  // Monthly breakdown state
  selectedMonth: string | null;
  messageDateFilter: MessageDateFilter | null;
  availableMonths: MonthOption[];
  // Regular chat functions
  fetchMessages: (organization_id: string, limit: number, before_id?: string | null, after_id?: string | null, month?: string | null) => Promise<number>;
  fetchNewMessages: (organization_id: string, limit: number) => Promise<void>;
  addMessage: (data: AddChatMessageRequest) => Promise<void>;
  appendMessage: (msg: ChatMessage) => void;
  clearMessages: () => void;
  connectWebSocket: (organizationId: string, accessToken: string) => void;
  disconnectWebSocket: () => void;
  setWebSocketConnected: (connected: boolean) => void;
  // Chatbot functions
  startChatbot: (organization_id: string) => Promise<void>;
  navigateChatbotOption: (data: ChatbotNavigateRequest) => Promise<void>;
  resetChatbot: () => void;
  // Monthly breakdown functions
  setSelectedMonth: (organizationId: string, month: string | null) => Promise<void>;
  setMessageDateFilter: (organizationId: string, filter: MessageDateFilter | null) => Promise<void>;
  buildAvailableMonths: () => void;
  // Read receipts functions
  updateLastReadMessage: (organization_id: string, message_id: string) => Promise<void>;
  fetchReadDetails: (organization_id: string, message_id: string) => Promise<ReadDetailsResponse | null>;
  updateReadCounts: (last_read_message_id: string, user_id: string) => void;
  // Thread functions
  openThread: (organizationId: string, parentMessage: ChatMessage) => Promise<void>;
  closeThread: () => void;
  fetchThreadReplies: (organizationId: string, parentMessageId: string) => Promise<void>;
  addThreadReply: (data: AddChatMessageRequest) => Promise<void>;
  incrementThreadReplyCount: (parentMessageId: string) => void;
  // Edit message function
  editMessage: (organizationId: string, messageId: string, newMessage: string) => Promise<void>;
  applyMessageEdit: (messageId: string, newMessage: string, editedAt: string) => void;
  // Mention functions
  orgMembers: OrgMember[];
  mentionNotification: { message_id: string; from_user_name: string; preview: string } | null;
  fetchOrgMembers: (organizationId: string) => Promise<void>;
  clearMentionNotification: () => void;
  // Search state
  searchQuery: string;
  searchResults: ChatSearchResult[];
  isSearching: boolean;
  activeSearchIndex: number;
  highlightedMessageId: string | null;
  // Search functions
  searchMessages: (organization_id: string, query: string) => Promise<void>;
  clearSearch: () => void;
  gotoSearchResult: (organization_id: string, index: number) => Promise<void>;
  scrollToMessage: (organization_id: string, messageId: string) => Promise<void>;
  setHighlightedMessage: (messageId: string | null) => void;
  // Pin state
  pinnedMessages: ChatMessage[];
  isLoadingPinned: boolean;
  pinInFlightId: string | null;
  fetchPinnedMessages: (organization_id: string) => Promise<void>;
  togglePinMessage: (organization_id: string, message_id: string, is_pinned: boolean) => Promise<void>;
  applyMessagePinFlag: (message_id: string, is_pinned: boolean) => void;
}

// POST responses return created_at with 'Z' suffix (UTC), but GET responses return without 'Z'.
// formatTimestamp is built for the no-Z format (server UTC treated as local by the browser).
// Stripping 'Z' makes timestamps consistent so no double-conversion happens.
const getMessageType = (data: AddChatMessageRequest): ChatMessageType => {
  if (data.audio_file_path) return 'voice';
  if (data.message?.startsWith('[FILE]')) return 'file';
  return 'text';
};

const reportMessageSent = (data: AddChatMessageRequest, isThreadReply: boolean) => {
  trackChatMessageSent({
    orgId: data.organization_id,
    messageType: getMessageType(data),
    isQuoteReply: !!data.quoted_message_id,
    isThreadReply,
    hasMentions: (data.mentioned_user_ids?.length ?? 0) > 0,
    voiceDuration: data.audio_duration ?? null,
  });
};

const normalizeMessage = (msg: ChatMessage): ChatMessage => ({
  ...msg,
  created_at: msg.created_at?.endsWith('Z')
    ? msg.created_at.slice(0, -1)
    : msg.created_at,
});


const toDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const messageDateFromKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const monthsBetween = (startKey: string, endKey: string): string[] => {
  const start = messageDateFromKey(startKey);
  const end = messageDateFromKey(endKey);
  const months: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

const monthsForYear = (year: number): string[] => {
  const now = new Date();
  if (year > now.getFullYear()) return [];
  const maxMonth = year === now.getFullYear() ? now.getMonth() + 1 : 12;
  return Array.from({ length: maxMonth }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
};

const filterMessagesByDateFilter = (messages: ChatMessage[], filter: MessageDateFilter): ChatMessage[] => {
  return messages.filter((msg) => {
    const msgDate = new Date(msg.created_at);
    const msgKey = toDateKey(msgDate);
    switch (filter.mode) {
      case 'day':
        return msgKey === filter.date;
      case 'month':
        return msgKey.slice(0, 7) === filter.month;
      case 'year':
        return msgDate.getFullYear() === filter.year;
      case 'custom':
        return msgKey >= filter.startDate && msgKey <= filter.endDate;
      default:
        return true;
    }
  });
};

const fetchMessagesForMonths = async (organization_id: string, months: string[]): Promise<ChatMessage[]> => {
  if (months.length === 0) return [];
  const responses = await Promise.all(
    months.map((month) => getChatMessages(organization_id, 10, null, null, month).catch(() => ({ result_list: [] as ChatMessage[] })))
  );
  const merged = responses.flatMap((r) => r.result_list.map(normalizeMessage));
  const byId = new Map<string, ChatMessage>();
  merged.forEach((msg) => byId.set(msg.id, msg));
  return Array.from(byId.values()).sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
};

let pinnedFetchSeq = 0;

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  currentOrgId: null,
  isLoading: false,
  error: null,
  isWebSocketConnected: false,
  _wsUnsubscribers: [],
  // Chatbot initial state
  isTreeMode: false,
  currentNodeId: null,
  chatbotTitle: '',
  chatbotMessage: '',
  chatbotOptions: [],
  showBackToMain: false,
  // Read receipts initial state
  organizationMembersCount: 0,
  // Thread initial state
  isThreadOpen: false,
  activeThreadParentId: null,
  threadParentMessage: null,
  threadReplies: [],
  isLoadingThread: false,
  // Monthly breakdown initial state
  selectedMonth: null,
  messageDateFilter: null,
  availableMonths: [],
  // Mention initial state
  orgMembers: [],
  mentionNotification: null,
  // Search initial state
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  activeSearchIndex: -1,
  highlightedMessageId: null,
  pinnedMessages: [],
  isLoadingPinned: false,
  pinInFlightId: null,

  fetchMessages: async (organization_id, limit, before_id, after_id, month) => {
    const isPaginating = Boolean(before_id);
    set({ error: null, ...(isPaginating ? {} : { isLoading: true }) });
    try {
      const response = await getChatMessages(organization_id, limit, before_id, after_id, month);
      const currentMessages = get().messages;
      const newMessages = response.result_list;
      
      // Update organization members count
      if (response.organization_members_count) {
        set({ organizationMembersCount: response.organization_members_count });
      }
      
      if (newMessages.length === 0) {
        if (!isPaginating) set({ isLoading: false });
        return 0;
      }

      if (before_id) {
        // Pagination: prepend only messages that are not already in the list
        const existingIds = new Set(currentMessages.map(msg => msg.id));
        const toPrepend = newMessages.filter((msg: ChatMessage) => !existingIds.has(msg.id));
        set({ messages: [...toPrepend, ...currentMessages] });
        return toPrepend.length;
      }

      set({ messages: newMessages, currentOrgId: organization_id, isLoading: false });
      return newMessages.length;
    } catch (error: any) {
      set({ error: error.message || 'Error fetching messages', isLoading: false });
      return 0;
    }
  },

  fetchNewMessages: async (organization_id, limit) => {
    try {
      const response = await getChatMessages(organization_id, limit, null, null);
      const currentMessages = get().messages;
      const newMessages = response.result_list;
      
      if (newMessages.length === 0) {
        return;
      }

      const existingMessageIds = new Set(currentMessages.map(msg => msg.id));
      const uniqueNewMessages = newMessages.filter((msg: ChatMessage) => !existingMessageIds.has(msg.id));
      
      if (uniqueNewMessages.length === 0) {
        return;
      }

      const mergedMessages = [...currentMessages, ...uniqueNewMessages];
      mergedMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      
      const finalMessages = mergedMessages.filter((msg, index, array) => 
        array.findIndex(m => m.id === msg.id) === index
      );
      
      set({ messages: finalMessages });
    } catch (error: any) {
      console.error('Error fetching new messages:', error);
    }
  },

  addMessage: async (data: AddChatMessageRequest) => {
    set({ error: null });
    try {
      const response = await addChatMessage(data);
      if (response.chat_message) {
        reportMessageSent(data, false);
        const normalized = normalizeMessage(response.chat_message);
        const currentMessages = get().messages;
        const messageExists = currentMessages.some(msg => msg.id === normalized.id);
        if (!messageExists) {
          set({ messages: [...currentMessages, normalized] });
        }
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      set({ error: error.message || 'Error sending message' });
      throw error;
    }
  },

  appendMessage: (msg: ChatMessage) => {
    set((state) => {
      const messageExists = state.messages.some(existingMsg => existingMsg.id === msg.id);
      if (messageExists) {
        return state;
      }
      return { messages: [...state.messages, msg] };
    });
  },

  clearMessages: () => {
    set({
      messages: [],
      currentOrgId: null,
      threadReplies: [],
      threadParentMessage: null,
      isThreadOpen: false,
      activeThreadParentId: null,
      pinnedMessages: [],
      isLoadingPinned: false,
      pinInFlightId: null,
    });
  },

  connectWebSocket: (organizationId: string, accessToken: string) => {
    // Clean up any existing handlers before adding new ones
    get()._wsUnsubscribers.forEach(unsub => unsub());

    chatWebSocketService.connect({
      organizationId,
      accessToken,
    });

    const unsubState = chatWebSocketService.onConnectionStateChange((connected) => {
      set({ isWebSocketConnected: connected });
    });

    const unsubMsg = chatWebSocketService.onMessage(async (message) => {
      if (message.type === 'new_message') {
        try {
          await get().fetchNewMessages(organizationId, 10);
        } catch (error) {
          // Silent error handling
        }
      } else if (message.type === 'last_read_update') {
        const { user_id, last_read_message_id } = message;
        get().updateReadCounts(last_read_message_id, user_id);
      } else if (message.type === 'thread_reply') {
        const { thread_parent_id } = message;
        const { isThreadOpen, activeThreadParentId } = get();

        if (isThreadOpen && activeThreadParentId === thread_parent_id) {
          try {
            const response = await getThreadReplies(organizationId, thread_parent_id);
            set({ threadReplies: response.replies });
            set((state) => ({
              messages: state.messages.map(msg =>
                msg.id === thread_parent_id
                  ? { ...msg, thread_replies_count: response.total_replies }
                  : msg
              )
            }));
          } catch (error) {
            // Silent
          }
        } else {
          get().incrementThreadReplyCount(thread_parent_id);
        }
      } else if (message.type === 'message_edited') {
        const { message_id, new_message, edited_at } = message;
        get().applyMessageEdit(message_id, new_message, edited_at);
      } else if (message.type === 'mention') {
        const { message_id, from_user_name, preview } = message;
        set({ mentionNotification: { message_id, from_user_name, preview } });
      } else if (message.type === 'message_pinned') {
        get().applyMessagePinFlag(message.message_id, Boolean(message.is_pinned));
        void get().fetchPinnedMessages(organizationId);
      }
    });

    set({ _wsUnsubscribers: [unsubState, unsubMsg] });
  },

  disconnectWebSocket: () => {
    get()._wsUnsubscribers.forEach(unsub => unsub());
    set({ _wsUnsubscribers: [] });
    chatWebSocketService.disconnect();
    set({ isWebSocketConnected: false });
  },

  setWebSocketConnected: (connected: boolean) => {
    set({ isWebSocketConnected: connected });
  },

  // ===== Chatbot functions =====

  startChatbot: async (organization_id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getChatbotWelcome(organization_id);
      set({
        isTreeMode: response.is_tree_mode,
        currentNodeId: response.node_id,
        chatbotTitle: response.title,
        chatbotMessage: response.message,
        chatbotOptions: response.options,
        showBackToMain: response.show_back_to_main,
        isLoading: false,
      });
      
      await get().fetchMessages(organization_id, CHAT_PAGE_SIZE, null, null);
    } catch (error: any) {
      console.error('Error starting chatbot:', error);
      set({ error: error.message || 'Error starting chatbot', isLoading: false });
    }
  },

  navigateChatbotOption: async (data: ChatbotNavigateRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await navigateChatbot(data);
      
      if (response.is_tree_mode) {
        set({
          isTreeMode: true,
          currentNodeId: response.node_id || null,
          chatbotTitle: response.title || '',
          chatbotMessage: response.message || '',
          chatbotOptions: response.options || [],
          showBackToMain: response.show_back_to_main || false,
          isLoading: false,
        });
      } else {
        set({
          isTreeMode: false,
          chatbotOptions: [],
          isLoading: false,
        });
      }
      
      await get().fetchMessages(data.organization_id, CHAT_PAGE_SIZE, null, null);
    } catch (error: any) {
      console.error('Error navigating chatbot:', error);
      set({ error: error.message || 'Error navigating chatbot', isLoading: false });
    }
  },

  resetChatbot: () => {
    set({
      isTreeMode: false,
      currentNodeId: null,
      chatbotTitle: '',
      chatbotMessage: '',
      chatbotOptions: [],
      showBackToMain: false,
    });
  },

  // ===== Monthly Breakdown functions =====

  buildAvailableMonths: () => {
    const messages = get().messages;
    if (messages.length === 0) return;

    const monthSet = new Set<string>();
    messages.forEach(msg => {
      const date = new Date(msg.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthSet.add(key);
    });

    // Also add last 12 months so user can browse even if no messages yet
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthSet.add(key);
    }

    const sorted = Array.from(monthSet).sort((a, b) => b.localeCompare(a));

    const months: MonthOption[] = sorted.map(value => {
      const [year, month] = value.split('-');
      const date = new Date(Number(year), Number(month) - 1, 1);
      return {
        value,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      };
    });

    set({ availableMonths: months });
  },

  setSelectedMonth: async (organizationId: string, month: string | null) => {
    if (!month) {
      await get().setMessageDateFilter(organizationId, null);
      return;
    }
    await get().setMessageDateFilter(organizationId, { mode: 'month', month });
  },

  setMessageDateFilter: async (organizationId: string, filter: MessageDateFilter | null) => {
    if (!filter) {
      set({ selectedMonth: null, messageDateFilter: null, messages: [] });
      await get().fetchMessages(organizationId, CHAT_PAGE_SIZE);
      return;
    }

    set({ selectedMonth: filter.mode === 'month' ? filter.month : '__filtered__', messageDateFilter: filter, messages: [], isLoading: true, error: null });

    try {
      let monthsToFetch: string[] = [];
      switch (filter.mode) {
        case 'month':
          monthsToFetch = [filter.month];
          break;
        case 'day':
          monthsToFetch = [filter.date.slice(0, 7)];
          break;
        case 'year':
          monthsToFetch = monthsForYear(filter.year);
          break;
        case 'custom':
          monthsToFetch = monthsBetween(filter.startDate, filter.endDate);
          break;
      }

      const loaded = await fetchMessagesForMonths(organizationId, monthsToFetch);
      const filtered = filterMessagesByDateFilter(loaded, filter);
      set({ messages: filtered, currentOrgId: organizationId, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Error fetching messages', isLoading: false });
    }
  },

  // ===== Read Receipts functions =====

  updateLastReadMessage: async (organization_id: string, message_id: string) => {
    try {
      await updateLastRead({ organization_id, message_id });
    } catch (error: any) {
      console.error('Error updating last read:', error);
    }
  },

  fetchReadDetails: async (organization_id: string, message_id: string) => {
    try {
      const response = await getReadDetails(organization_id, message_id);
      return response;
    } catch (error: any) {
      console.error('Error fetching read details:', error);
      return null;
    }
  },

  updateReadCounts: (last_read_message_id: string, user_id: string) => {
    set((state) => {
      const updatedMessages = state.messages.map(msg => {
        const messageIndex = state.messages.findIndex(m => m.id === msg.id);
        const lastReadIndex = state.messages.findIndex(m => m.id === last_read_message_id);
        
        if (messageIndex <= lastReadIndex && msg.read_by_count !== undefined) {
          return {
            ...msg,
            read_by_count: msg.read_by_count + 1
          };
        }
        return msg;
      });
      
      return { messages: updatedMessages };
    });
  },

  // ===== Thread functions =====

  openThread: async (organizationId: string, parentMessage: ChatMessage) => {
    set({
      isThreadOpen: true,
      activeThreadParentId: parentMessage.id,
      threadParentMessage: parentMessage,
      threadReplies: [],
      isLoadingThread: true,
    });
    try {
      const response = await getThreadReplies(organizationId, parentMessage.id);
      set({
        threadReplies: response.replies,
        threadParentMessage: response.parent_message,
        isLoadingThread: false,
      });
    } catch (error: any) {
      console.error('Error fetching thread replies:', error);
      set({ isLoadingThread: false });
    }
  },

  closeThread: () => {
    set({
      isThreadOpen: false,
      activeThreadParentId: null,
      threadParentMessage: null,
      threadReplies: [],
    });
  },

  fetchThreadReplies: async (organizationId: string, parentMessageId: string) => {
    set({ isLoadingThread: true });
    try {
      const response = await getThreadReplies(organizationId, parentMessageId);
      set({
        threadReplies: response.replies,
        threadParentMessage: response.parent_message,
        isLoadingThread: false,
      });
    } catch (error: any) {
      console.error('Error fetching thread replies:', error);
      set({ isLoadingThread: false });
    }
  },

  addThreadReply: async (data: AddChatMessageRequest) => {
    try {
      const response = await addChatMessage(data);
      if (response.chat_message) {
        reportMessageSent(data, true);
        const normalized = normalizeMessage(response.chat_message);
        const currentReplies = get().threadReplies;
        const replyExists = currentReplies.some(r => r.id === normalized.id);
        if (!replyExists) {
          set({ threadReplies: [...currentReplies, normalized] });
        }
        // Update reply count in main messages
        if (data.thread_parent_id) {
          get().incrementThreadReplyCount(data.thread_parent_id);
        }
      }
    } catch (error: any) {
      console.error('Error sending thread reply:', error);
      throw error;
    }
  },

  incrementThreadReplyCount: (parentMessageId: string) => {
    set((state) => ({
      messages: state.messages.map(msg =>
        msg.id === parentMessageId
          ? { ...msg, thread_replies_count: (msg.thread_replies_count || 0) + 1 }
          : msg
      )
    }));
  },

  // ===== Mention functions =====

  fetchOrgMembers: async (organizationId: string) => {
    try {
      const members = await getOrgMembers(organizationId);
      set({ orgMembers: members });
    } catch (error: any) {
      console.error('Error fetching org members:', error);
    }
  },

  clearMentionNotification: () => set({ mentionNotification: null }),

  // ===== Edit Message functions =====

  editMessage: async (organizationId: string, messageId: string, newMessage: string) => {
    try {
      const response = await editChatMessage(organizationId, messageId, { message: newMessage });
      get().applyMessageEdit(response.message_id, response.message, response.edited_at);
    } catch (error: any) {
      console.error('Error editing message:', error);
      throw error;
    }
  },

  applyMessageEdit: (messageId: string, newMessage: string, editedAt: string) => {
    set((state) => ({
      messages: state.messages.map(msg =>
        msg.id === messageId
          ? { ...msg, message: newMessage, is_edited: true, edited_at: editedAt }
          : msg
      ),
      threadReplies: state.threadReplies.map(reply =>
        reply.id === messageId
          ? { ...reply, message: newMessage, is_edited: true, edited_at: editedAt }
          : reply
      ),
      threadParentMessage: state.threadParentMessage?.id === messageId
        ? { ...state.threadParentMessage, message: newMessage, is_edited: true, edited_at: editedAt }
        : state.threadParentMessage,
      pinnedMessages: state.pinnedMessages.map((msg) =>
        msg.id === messageId
          ? { ...msg, message: newMessage, is_edited: true, edited_at: editedAt }
          : msg,
      ),
    }));
  },

  // ===== Search functions =====

  searchMessages: async (organization_id: string, query: string) => {
    const trimmed = query.trim();
    set({ searchQuery: query });

    if (!trimmed) {
      set({ searchResults: [], activeSearchIndex: -1, isSearching: false });
      return;
    }

    set({ isSearching: true });
    try {
      const response = await searchChatMessages(organization_id, trimmed);
      const results = response.results ?? [];
      set({
        searchResults: results,
        activeSearchIndex: -1,
        isSearching: false,
      });
      // Jump to the first (most relevant) result automatically
      if (results.length > 0) {
        await get().gotoSearchResult(organization_id, 0);
      }
    } catch (error: any) {
      console.error('Error searching messages:', error);
      set({ searchResults: [], activeSearchIndex: -1, isSearching: false });
    }
  },

  clearSearch: () => {
    set({ searchQuery: '', searchResults: [], activeSearchIndex: -1, isSearching: false, highlightedMessageId: null });
  },

  scrollToMessage: async (organization_id: string, messageId: string) => {
    const existing = get().messages.some(m => m.id === messageId);
    if (existing) {
      set({ highlightedMessageId: messageId });
      return;
    }

    set({ isLoading: true });
    try {
      const response = await getMessageContext(organization_id, messageId, 20, 20);
      const normalized = response.result_list.map(normalizeMessage);
      set({
        messages: normalized,
        currentOrgId: organization_id,
        selectedMonth: null,
        messageDateFilter: null,
        highlightedMessageId: messageId,
        isLoading: false,
      });
      if (response.organization_members_count) {
        set({ organizationMembersCount: response.organization_members_count });
      }
    } catch (error: any) {
      console.error('Error loading message context:', error);
      set({ error: error.message || 'Error loading message context', isLoading: false });
    }
  },

  gotoSearchResult: async (organization_id: string, index: number) => {
    const { searchResults } = get();
    if (index < 0 || index >= searchResults.length) return;

    const target = searchResults[index];
    set({ activeSearchIndex: index });
    await get().scrollToMessage(organization_id, target.id);
  },

  setHighlightedMessage: (messageId: string | null) => set({ highlightedMessageId: messageId }),

  fetchPinnedMessages: async (organization_id) => {
    const fetchId = ++pinnedFetchSeq;
    set({ isLoadingPinned: true });
    try {
      const response = await getPinnedChatMessages(organization_id);
      if (fetchId !== pinnedFetchSeq) return;
      const pinned = response.result_list.map(normalizeMessage);
      const pinnedIds = new Set(pinned.map((msg) => msg.id));
      set((state) => ({
        pinnedMessages: pinned,
        isLoadingPinned: false,
        messages: state.messages.map((msg) => {
          if (pinnedIds.has(msg.id)) return { ...msg, is_pinned: true };
          if (msg.is_pinned) return { ...msg, is_pinned: false };
          return msg;
        }),
        threadParentMessage: state.threadParentMessage
          ? {
              ...state.threadParentMessage,
              is_pinned: pinnedIds.has(state.threadParentMessage.id),
            }
          : state.threadParentMessage,
      }));
    } catch (error: unknown) {
      console.error('Error fetching pinned messages:', error);
      if (fetchId !== pinnedFetchSeq) return;
      set({ isLoadingPinned: false });
    }
  },

  applyMessagePinFlag: (message_id, is_pinned) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === message_id ? { ...msg, is_pinned } : msg,
      ),
      threadReplies: state.threadReplies.map((reply) =>
        reply.id === message_id ? { ...reply, is_pinned } : reply,
      ),
      threadParentMessage: state.threadParentMessage?.id === message_id
        ? { ...state.threadParentMessage, is_pinned }
        : state.threadParentMessage,
    }));
  },

  togglePinMessage: async (organization_id, message_id, is_pinned) => {
    if (get().pinInFlightId === message_id) return;
    const previousPinned = get().pinnedMessages;
    const previousMessages = get().messages;
    const previousParent = get().threadParentMessage;
    const target = get().messages.find((msg) => msg.id === message_id)
      ?? (get().threadParentMessage?.id === message_id ? get().threadParentMessage : undefined);
    get().applyMessagePinFlag(message_id, is_pinned);
    set((state) => {
      let pinnedMessages = state.pinnedMessages;
      if (is_pinned && target && !state.pinnedMessages.some((msg) => msg.id === message_id)) {
        pinnedMessages = [...state.pinnedMessages, normalizeMessage({ ...target, is_pinned: true })];
      } else if (!is_pinned) {
        pinnedMessages = state.pinnedMessages.filter((msg) => msg.id !== message_id);
      }
      return { pinnedMessages, pinInFlightId: message_id };
    });
    let saved = false;
    try {
      await pinChatMessage(organization_id, message_id, { is_pinned });
      saved = true;
      await get().fetchPinnedMessages(organization_id);
    } catch (error: unknown) {
      console.error('Error toggling pin:', error);
      if (!saved) {
        set({
          pinnedMessages: previousPinned,
          messages: previousMessages,
          threadParentMessage: previousParent,
        });
        throw error;
      }
    } finally {
      set({ pinInFlightId: null });
    }
  },
}));
